export const thresholds = [89800, 119900, 159900, 199900, 249900];
export const protectorThreshold = 39900;
// The offer is advertised as Rs.899; two Rs.449 cases qualify at Rs.898.
export const rewardDisplayAmount = amount => amount === 89800 ? 89900 : amount;
export const progressThresholds = [protectorThreshold, ...thresholds];
export function milestoneStatus(state, amount, selectionReady = false) {
  if (amount === protectorThreshold) return !state.bonusExpected ? 'locked' : state.bonus.length === 1 ? 'reached' : 'adding';
  if (amount > state.subtotal) return 'locked';
  if (amount === state.tier) return selectionReady ? 'selected' : 'choose';
  return 'reached';
}
export const giftCap = 220000;
export function tierGiftWorth(tier, catalog, includeProtector = false) {
  if (tier === 249900) return {value:giftCap,upTo:true};
  const worth = group => Math.max(0,...catalog.filter(gift => gift.group === group && giftAllowed(gift)).map(gift => Number(gift.value)));
  if (tier === protectorThreshold) return {value:worth('protector'),upTo:false};
  const value = rewardGroups(tier).reduce((sum,group) => sum + worth(group),0);
  return {value:value + (includeProtector ? worth('protector') : 0),upTo:false};
}
export function giftSelectionKey(tier, gift) {
  return tier === 249900 && gift.group !== 'plaque' ? gift.variant : gift.group;
}
export function rewardTier(subtotal) {
  return [...thresholds].reverse().find(value => subtotal >= value) || 0;
}
export function rewardGroups(tier) {
  if (tier === 89900 || tier === 89800) return ['keychain']; // Accept gifts selected before the threshold changed.
  if (tier === 119900) return ['keychain','silicone'];
  if (tier === 159900 || tier === 149900) return ['keychain','silicone','cable']; // Retain eligible earlier selections.
  if (tier === 199900) return ['keychain','silicone','cable','glass'];
  if (tier === 249900) return ['keychain','silicone','cable','glass','plaque'];
  return [];
}
export function giftAllowed(gift) {
  if (!gift || /lamp/i.test([gift.title,gift.sourceType].join(' '))) return false;
  if (['silicone','glass'].includes(gift.group) && /luxury|ferrari/i.test([gift.title,gift.sourceType,...(gift.tags||[])].join(' '))) return false;
  if (!['keychain','silicone','glass','plaque','protector','cable'].includes(gift.group)) return false;
  return Number.isInteger(gift.value) && gift.value > 0 && gift.value <= giftCap;
}
export function isPaidPhoneCase(line) {
  return /(?:phone\s*case|glass.*case|silicone.*case|luxury.*case|ferrari.*case|wallet\s*case)/i.test([line.product_type,line.product_title,line.title].join(' '));
}
export function rewardState(cart, catalog) {
  const ids = new Set(catalog.map(item => String(item.variant)));
  const allGifts = cart.items.filter(item => ids.has(String(item.variant_id || item.id)) || String(item.properties?._festive_gift) === 'true' || /festive.gift/i.test(item.product_type||''));
  const giftKeys = new Set(allGifts.map(item => item.key));
  const paid = cart.items.filter(item => !giftKeys.has(item.key));
  const subtotal = paid.reduce((sum, item) => sum + Number(item.original_line_price ?? item.final_line_price ?? 0), 0);
  const tier = rewardTier(subtotal);
  const hasPaidPhoneCase = paid.some(line => Number(line.original_line_price ?? line.final_line_price ?? 0) > 0 && isPaidPhoneCase(line));
  const bonusExpected = subtotal >= protectorThreshold && hasPaidPhoneCase;
  const protector = catalog.find(item => item.group === 'protector' && giftAllowed(item));
  const bonus = allGifts.filter(line => String(line.variant_id || line.id) === String(protector?.variant) || line.properties?._festive_group === 'protector');
  const bonusKeys = new Set(bonus.map(line => line.key));
  const gifts = allGifts.filter(line => !bonusKeys.has(line.key));
  const total = tier === 249900 ? 3 : rewardGroups(tier).length;
  return {subtotal,tier,total,gifts,bonus,hasPaidPhoneCase,bonusExpected,bonusValue:bonusExpected ? Number(protector?.value||0) : 0};
}
export function selectionValid(tier, choices, bonusValue = 0) {
  const groups = choices.map(choice => typeof choice === 'string' ? choice : choice.group);
  if (tier === 249900) {
    if (choices.some(choice => typeof choice === 'string' || !giftAllowed(choice))) return false;
    return choices.length >= 1 && choices.length <= 3 && new Set(choices.map(choice => String(choice.variant))).size === choices.length
      && groups.every(group => ['keychain','silicone','glass','plaque','cable'].includes(group))
      && groups.filter(group => group === 'plaque').length <= 1
      && choices.reduce((sum,choice) => sum + Number(choice.value),bonusValue) <= giftCap;
  }
  const expected = rewardGroups(tier);
  return expected.length > 0 && groups.length === expected.length && new Set(groups).size === groups.length
    && expected.every(group => groups.includes(group));
}
export function invalidGiftKeys(state, catalog, phones) {
  const invalidBonus = (state.bonus||[]).filter(line => {
    const gift = catalog.find(item => String(item.variant) === String(line.variant_id || line.id));
    return !state.bonusExpected || state.bonus.length !== 1 || !giftAllowed(gift) || gift.group !== 'protector'
      || line.quantity !== 1 || Number(line.final_line_price) !== 0 || line.properties?._festive_group !== 'protector';
  }).map(line => line.key);
  if (!state.gifts.length) return invalidBonus;
  const choices = [];
  const invalid = [];
  for (const line of state.gifts) {
    const product = catalog.find(item => String(item.variant) === String(line.variant_id || line.id));
    const props = line.properties || {};
    const earnedTier = Number(props._festive_tier) * 100;
    let valid = giftAllowed(product) && line.quantity === 1 && Number(line.final_line_price) === 0
      && props._festive_group === product?.group && rewardGroups(earnedTier).includes(product?.group)
      && rewardGroups(state.tier).includes(product?.group);
    if (state.tier === 249900 && earnedTier !== state.tier) valid = false;
    if (valid && ['silicone','glass'].includes(product.group)) valid = !!phones[props['Phone Brand']]?.includes(props['Phone Model']);
    if (valid) valid = state.tier === 249900
      ? selectionValid(state.tier,[...choices,product],state.bonusValue)
      : !choices.some(choice => choice.group === product.group);
    if (valid) choices.push(product); else invalid.push(line.key);
  }
  return [...invalidBonus,...invalid];
}
export function selectionComplete(state, catalog, phones) {
  return state.gifts.length > 0 && !invalidGiftKeys(state,catalog,phones).length
    && selectionValid(state.tier,state.gifts.map(line => catalog.find(item => String(item.variant) === String(line.variant_id || line.id))),state.bonusValue);
}
export function missingRewardGroups(state, catalog) {
  if (state.tier === 249900) return [];
  const selected = state.gifts.map(line => catalog.find(item => String(item.variant) === String(line.variant_id || line.id))?.group);
  return rewardGroups(state.tier).filter(group => !selected.includes(group));
}
export function planCartQuantity(cart, key, quantity, catalog, phones) {
  const line=cart?.items?.find(item=>item.key===key);
  if(!line || !Number.isInteger(quantity) || quantity<0 || !line.quantity || line.item_components?.length || line.parent_relationship?.parent)return null;
  const items=cart.items.flatMap(item=>item.key!==key?[item]:quantity?[{...item,quantity,
    original_line_price:Math.round(Number(item.original_price??item.original_line_price/item.quantity)*quantity),
    final_line_price:Math.round(Number(item.final_line_price/item.quantity)*quantity)}]:[]);
  const projected={...cart,items};
  const removedGiftKeys=invalidGiftKeys(rewardState(projected,catalog),catalog,phones);
  const removed=new Set(removedGiftKeys);
  projected.items=items.filter(item=>!removed.has(item.key));
  projected.item_count=projected.items.reduce((sum,item)=>sum+item.quantity,0);
  // Increases keep Shopify's change endpoint and its inventory validation.
  // Reductions can remove the paid line and newly ineligible gifts atomically.
  const updates=quantity<=line.quantity?Object.fromEntries([[key,quantity],...removedGiftKeys.map(key=>[key,0])]):null;
  return {cart:projected,removedGiftKeys,updates};
}
export async function reconcileProtector({request,cart,catalog,sectionParams={},cartFromSections=()=>null}) {
  const state = rewardState(cart,catalog);
  if (!state.bonusExpected || state.bonus.length) return {cart,changed:false};
  const gift = catalog.find(item => item.group === 'protector' && giftAllowed(item));
  if (!gift) throw new Error('Cable protector unavailable');
  const added=await request('cart/add.js',{items:[{id:gift.variant,quantity:1,properties:{_festive_gift:'true',_festive_group:'protector',_festive_tier:'base','Festive reward':'FREE GIFT'}}],...sectionParams});
  return {cart:cartFromSections(added?.sections)||await request('cart.js'),changed:true,sections:added?.sections};
}

const sameGift = (line, saved) => String(line.variant_id || line.id) === String(saved.variant_id || saved.id)
  && JSON.stringify(Object.entries(line.properties || {}).sort()) === JSON.stringify(Object.entries(saved.properties || {}).sort());

export async function recoverGiftSelection({request, journal, catalog, phones}) {
  let cart = await request('cart.js');
  if (journal.cartToken && cart.token !== journal.cartToken) return {cart, restored:false};
  const added = cart.items.filter(line => line.properties?._festive_selection === journal.transaction);
  if (added.length) cart = await request('cart/update.js',{updates:Object.fromEntries(added.map(line=>[line.key,0]))});
  let current = rewardState(cart,catalog);
  const eligible = current.tier === journal.tier;
  if (eligible && journal.previous.length) {
    const missing = journal.previous.filter(saved => !cart.items.some(line => sameGift(line,saved)));
    if (missing.length) {
      try {
        await request('cart/add.js',{items:missing.map(saved=>({id:saved.variant_id || saved.id,quantity:saved.quantity,properties:{...saved.properties}}))});
      } catch(error) {
        if(error.status!==422 && error.status!==400)throw error;
        cart=await request('cart.js');
        if(journal.previous.some(saved=>!cart.items.some(line=>sameGift(line,saved)&&line.quantity===saved.quantity))) {
          const invalid=invalidGiftKeys(rewardState(cart,catalog),catalog,phones);
          if(invalid.length)cart=await request('cart/update.js',{updates:Object.fromEntries(invalid.map(key=>[key,0]))});
          return {cart,restored:false,unavailable:true};
        }
      }
      cart = await request('cart.js');
    }
    current = rewardState(cart,catalog);
    const invalid=invalidGiftKeys(current,catalog,phones);
    if (current.tier !== journal.tier || invalid.length
      || journal.previous.some(saved=>!cart.items.some(line=>sameGift(line,saved)&&line.quantity===saved.quantity))) {
      if(invalid.length)cart=await request('cart/update.js',{updates:Object.fromEntries(invalid.map(key=>[key,0]))});
      return {cart,restored:false,tierChanged:current.tier!==journal.tier,unavailable:current.tier===journal.tier};
    }
  } else {
    const invalid = invalidGiftKeys(current,catalog,phones);
    if (invalid.length) cart=await request('cart/update.js',{updates:Object.fromEntries(invalid.map(key=>[key,0]))});
  }
  return {cart, restored:eligible && journal.previous.length>0, tierChanged:!eligible};
}

export async function replaceGiftSelection({request, choices, tier, catalog, phones, transaction, saveRecovery, clearRecovery}) {
  const before=await request('cart.js');
  const previous=rewardState(before,catalog);
  if(previous.tier!==tier) {const error=new Error('Cart tier changed');error.tierChanged=true;throw error;}
  const journal={transaction,tier,cartToken:before.token,previous:invalidGiftKeys(previous,catalog,phones).length?[]:previous.gifts};
  saveRecovery(journal);
  try {
    // Keep the old lines until the new set has actually arrived at zero price.
    // A unique private property separates even an unchanged variant/model.
    await request('cart/add.js',{items:choices.map(choice=>({...choice,properties:{...choice.properties,_festive_selection:transaction}}))});
    let cart=await request('cart.js');
    const next=rewardState(cart,catalog);
    const added=next.gifts.filter(line=>line.properties?._festive_selection===transaction);
    if(next.tier!==tier || added.length!==choices.length
      || choices.some(choice=>!added.some(line=>String(line.variant_id || line.id)===String(choice.id)))
      || !selectionComplete({...next,gifts:added},catalog,phones)) throw new Error('Replacement could not be verified');
    const old=next.gifts.filter(line=>line.properties?._festive_selection!==transaction);
    if(old.length) await request('cart/update.js',{updates:Object.fromEntries(old.map(line=>[line.key,0]))});
    cart=await request('cart.js');
    const final=rewardState(cart,catalog);
    if(final.tier!==tier || final.gifts.length!==choices.length || final.gifts.some(line=>line.properties?._festive_selection!==transaction) || !selectionComplete(final,catalog,phones)) throw new Error('Cart changed during replacement');
    clearRecovery();
    return cart;
  } catch(cause) {
    const error=new Error(cause.message || 'Gift replacement failed');
    try {
      const recovered=await recoverGiftSelection({request,journal,catalog,phones});
      clearRecovery();
      Object.assign(error,recovered);
    } catch { error.recoveryPending=true; }
    throw error;
  }
}

if (typeof document !== 'undefined') {
  const config = JSON.parse(document.getElementById('festive-rewards-config').textContent);
  const components = new Set();
  const root = window.Shopify?.routes?.root || '/';
  const money = amount => new Intl.NumberFormat('en-IN', {style:'currency', currency:'INR', maximumFractionDigits:0}).format(amount / 100);
  const t = (key, values = {}) => Object.entries(values).reduce((text, [name, value]) => text.replaceAll('{' + name + '}', String(value)), config.copy[key] || config.copy[key.replace(/1599$/, '1499').replace(/899$/, '898')] || key);
  const node = (tag, className, text) => { const element = document.createElement(tag); element.className = className; if (text) element.textContent = text; return element; };
  let state;
  let pending;
  let queued = false;
  let timer;
  let busy = false;
  let lastTier;
  let revision = 0;
  let latestCart;
  let cartDirty=true;
  let cartChanging=false;
  let restoreSections=false;
  let mutationNotice;
  let recovery;
  const recoveryKey='fg-gift-recovery';
  try {recovery=JSON.parse(sessionStorage.getItem(recoveryKey)||'null');} catch {}
  const saveRecovery=journal=>{recovery=journal;try{sessionStorage.setItem(recoveryKey,JSON.stringify(journal));}catch{}};
  const clearRecovery=()=>{recovery=undefined;try{sessionStorage.removeItem(recoveryKey);}catch{}};
  // Gift updates can reorder native cart lines. Pause row actions until fresh sections arrive.
  const lockCheckout=()=>{
    document.querySelectorAll('[data-fg-open],[data-fg-tier-action]').forEach(button=>button.disabled=busy||cartChanging||restoreSections);
    document.querySelectorAll('.cart-drawer__summary,.cart-page__summary,.cart-items__table').forEach(element=>{
    const unsettled=state&&(invalidGiftKeys(state,config.catalog,config.phones).length||(state.bonusExpected&&!state.bonus.length));
    if(busy||recovery||cartChanging||restoreSections||unsettled) {if(!element.hasAttribute('inert')){element.setAttribute('data-fg-lock','');element.inert=true;}}
    else if(element.hasAttribute('data-fg-lock')){element.inert=false;element.removeAttribute('data-fg-lock');}
    });
  };
  const request = async (path, body) => {
    const response = await fetch(root + path, {method:body ? 'POST' : 'GET', headers:{Accept:'application/json', ...(body ? {'Content-Type':'application/json'} : {})}, ...(body ? {body:JSON.stringify(body)} : {}), cache:'no-store',signal:AbortSignal.timeout(15000)});
    const data = await response.json();
    if (!response.ok) {const error=new Error(data.description || t('failed'));error.status=response.status;throw error;}
    return data;
  };
  const error = message => components.forEach(component => component.error(message));
  const sectionParams=()=>({sections:[...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(element=>element.dataset.sectionId))].filter(Boolean).slice(0,5),sections_url:window.location.pathname});
  const cartFromSections=sections=>{
    for(const html of Object.values(sections||{})){
      if(typeof html!=='string')continue;
      try{
        const source=new DOMParser().parseFromString(html,'text/html').querySelector('[data-fg-cart]');
        const cart=JSON.parse(source?.textContent||'null');
        if(Array.isArray(cart?.items)&&typeof cart.item_count==='number'&&typeof cart.total_price==='number')return cart;
      }catch{}
    }
    return null;
  };
  const refreshSections = async (provided) => {
    const targets = [...document.querySelectorAll('cart-items-component[data-section-id]')];
    const ids = [...new Set(targets.map(element => element.dataset.sectionId))].filter(Boolean).slice(0,5);
    if (!ids.length) { window.location.reload(); return; }
    const url = new URL(window.location.href); url.searchParams.set('sections',ids.join(','));
    let sections=provided;
    if(!sections || !ids.every(id=>typeof sections[id]==='string')){
      const response = await fetch(url, {cache:'no-store',signal:AbortSignal.timeout(15000)});
      if (!response.ok) throw new Error(t('failed'));
      sections = await response.json();
    }
    const parsed = Object.values(sections).filter(Boolean).map(html => new DOMParser().parseFromString(html,'text/html'));
    for (const target of targets) {
      const selector = 'cart-items-component[data-section-id="' + CSS.escape(target.dataset.sectionId) + '"]';
      const replacement = parsed.map(doc => doc.querySelector(selector)).find(Boolean);
      if (replacement) target.replaceWith(replacement.cloneNode(true));
    }
    for (const icon of document.querySelectorAll('cart-icon')) {
      const replacement = parsed.map(doc => doc.querySelector('cart-icon')).find(Boolean);
      if (replacement) icon.replaceWith(replacement.cloneNode(true));
    }
  };
  const celebrate = tier => {
    if (lastTier !== undefined && tier > lastTier) {
      let previous = 0;
      try { previous = Number(sessionStorage.getItem('fg-celebrated') || 0); } catch {}
      if (tier > previous) {
        try { sessionStorage.setItem('fg-celebrated',String(tier)); } catch {}
        document.querySelector('.fg-toast')?.remove();
        const toast = node('div','fg-toast'); toast.setAttribute('role','status');
        toast.append(node('span','fg-toast__icon','🎉'),node('strong','',t('unlocked'+tier/100)));
        const close = node('button','', '×'); close.type='button'; close.setAttribute('aria-label',t('close')); close.onclick=()=>toast.remove(); toast.append(close);
        document.body.append(toast); setTimeout(()=>toast.remove(),5200);
      }
    }
    lastTier = tier;
  };
  let latestCartCount;
  const syncCartCount=count=>{
    latestCartCount=count;
    for(const icon of document.querySelectorAll('cart-icon')){
      icon.renderCartBubble?.(count,false,false);
      const label=icon.querySelector('[ref="cartBubbleText"] > .visually-hidden');
      if(label)label.textContent=label.textContent.trim().replace(/\d+$/,String(count));
    }
  };
  customElements.whenDefined('cart-icon').then(()=>{if(latestCartCount!==undefined)syncCartCount(latestCartCount);});
  const applyCart = cart => {
    latestCart=cart;cartDirty=false;
    syncCartCount(cart.item_count);
    state = rewardState(cart,config.catalog);
    components.forEach(component=>component.render(state));
    lockCheckout();
    celebrate(state.tier);
  };
  const sync = async (fresh=false) => {
    if(fresh)cartDirty=true;
    if (pending || busy || cartChanging) { queued = true; return pending; }
    const startedAt = revision;
    pending = (async () => {
      let cart,recoveryNotice;
      if(recovery) {
        const recovered=await recoverGiftSelection({request,journal:recovery,catalog:config.catalog,phones:config.phones});
        clearRecovery();lockCheckout();cart=recovered.cart;if(recovered.unavailable)recoveryNotice=t('previousUnavailable');await refreshSections();
      } else cart=cartDirty||!latestCart?await request('cart.js'):latestCart;
      // A newer cart response from the theme must win over an older read.
      if (startedAt !== revision) { queued=true; return; }
      state = rewardState(cart,config.catalog);
      const invalid = invalidGiftKeys(state,config.catalog,config.phones);
      if (invalid.length) {
        lockCheckout();
        cart = await request('cart/update.js',{updates:Object.fromEntries(invalid.map(key=>[key,0])),...sectionParams()});
        state = rewardState(cart,config.catalog);
        await refreshSections(cart.sections);
      }
      const bonusResult=await reconcileProtector({request,cart,catalog:config.catalog,sectionParams:sectionParams(),cartFromSections});
      cart=bonusResult.cart;
      if(bonusResult.changed||restoreSections)await refreshSections(bonusResult.sections);
      restoreSections=false;
      applyCart(cart);
      if(recoveryNotice||mutationNotice){error(recoveryNotice||mutationNotice);mutationNotice=undefined;}
    })().catch(()=>{lockCheckout();error(t(recovery?'recoveryPending':'network'));}).finally(()=>{ pending=undefined; if(queued) {queued=false;schedule();} });
    return pending;
  };
  const schedule = () => { clearTimeout(timer); timer=setTimeout(sync,0); };
  class FestiveRewards extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected=true; components.add(this);
      this.selected = new Map();
      this.bindControls();
      this.observer = new MutationObserver(() => {
        if (this.dialog !== this.querySelector('[data-fg-dialog]')) {
          this.selected.clear();
          this.bindControls();
          if (state) this.render(state);
          schedule();
        }
      });
      this.observer.observe(this,{childList:true,subtree:true});
      const initial = this.querySelector('[data-fg-cart]');
      if (!state && initial) {
        try { latestCart=JSON.parse(initial.textContent);state=rewardState(latestCart,config.catalog);lastTier=state.tier; } catch {}
      }
      if (state) this.render(state);
      lockCheckout();
      schedule();
    }
    bindControls() {
      this.dialog = this.querySelector('[data-fg-dialog]');
      this.querySelector('[data-fg-open]').onclick=()=>this.open();
      this.querySelector('[data-fg-close]').onclick=()=>{if(!busy)this.dialog.close();};
      this.querySelector('[data-fg-retry]').onclick=()=>sync();
      this.querySelector('[data-fg-add]').onclick=()=>this.add();
      this.querySelector('[data-fg-search]').oninput=event=>{clearTimeout(this.searchTimer);this.searchTimer=setTimeout(()=>this.renderGiftGroups(event.target.value),150);};
      this.dialog.addEventListener('cancel',event=>{if(busy)event.preventDefault();});
      this.dialog.addEventListener('click',event=>{if(!busy&&event.target===this.dialog) {const r=this.dialog.getBoundingClientRect(); if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)this.dialog.close();}});
    }
    disconnectedCallback() {clearTimeout(this.searchTimer);this.giftLoader?.disconnect();this.observer?.disconnect();components.delete(this); this.connected=false;}
    error(message) {
      const element=this.querySelector('[data-fg-error]');element.hidden=false;element.textContent=message;
      this.querySelector('[data-fg-retry]').hidden=false;
    }
    render(next) {
      const oldTier=this.tier;
      this.tier=next.tier;
      this.total=next.total;
      if(this.dialog.open && oldTier!==this.tier) {this.dialog.close();this.selected.clear();}
      const nextThreshold=progressThresholds.find(value=>value>next.subtotal);
      const previous=progressThresholds.filter(value=>value<=next.subtotal).at(-1)||0;
      const progress=nextThreshold ? Math.max(0,Math.min(100,(next.subtotal-previous)/(nextThreshold-previous)*100)) : 100;
      this.querySelector('[data-fg-heading]').textContent=t(next.tier?'unlocked'+next.tier/100:next.bonusExpected?'unlocked399':'initial');
      const earned=this.querySelector('[data-fg-earned]');earned.hidden=!next.tier;earned.textContent=next.tier?t('earned',{reward:t('reward'+next.tier/100)}):'';
      this.querySelector('[data-fg-status]').textContent=nextThreshold?t('away',{amount:money(nextThreshold-next.subtotal),reward:t('nextReward'+nextThreshold/100)}):t('complete');
      this.querySelector('[data-fg-fill]').style.width=progress+'%';
      this.querySelector('[data-fg-progress]').setAttribute('aria-valuenow',String(Math.round(progress)));
      this.querySelector('[data-fg-progress]').setAttribute('aria-valuetext',this.querySelector('[data-fg-status]').textContent);
      this.querySelector('[data-fg-start]').textContent=money(rewardDisplayAmount(previous));
      this.querySelector('[data-fg-end]').textContent=nextThreshold?money(rewardDisplayAmount(nextThreshold)):'✓';
      const bonus=this.querySelector('[data-fg-bonus]');bonus.hidden=!next.bonusExpected;bonus.textContent=t(next.bonus.length?'protectorAdded':'protectorPending');
      const button=this.querySelector('[data-fg-open]');button.hidden=!next.tier;
      const selectionReady=selectionComplete(next,config.catalog,config.phones);
      const missing=missingRewardGroups(next,config.catalog).length;
      const chooseKey=next.tier===249900?'chooseUpTo3':next.total===4?'choose4':next.total===3?'choose3':next.total===2?'choose2':'choose';
      const chooseText=next.gifts.length&&missing?t(missing===1?'chooseNewGift':'chooseMoreGifts',{count:missing}):t(chooseKey);
      button.textContent=selectionReady?t('selected')+' · '+t('change'):chooseText;
      for(const row of this.querySelectorAll('[data-fg-tier]')){
        const worth=tierGiftWorth(Number(row.dataset.fgTier),config.catalog,next.hasPaidPhoneCase);
        const total=row.querySelector('[data-fg-tier-total]');
        if(total){total.hidden=!worth.value;total.querySelector('[data-fg-tier-total-value]').textContent=(worth.upTo?'up to ':'')+money(worth.value);}
        const status=milestoneStatus(next,Number(row.dataset.fgTier),selectionReady);
        row.dataset.state=status;
        if(Number(row.dataset.fgTier)===next.tier&&next.tier)row.setAttribute('aria-current','step');else row.removeAttribute('aria-current');
        row.querySelector('.fg-tier-dot').textContent=['reached','selected'].includes(status)?'✓':'';
        const label=row.querySelector('[data-fg-tier-state]');
        label.textContent=t(status==='selected'?'tierSelected':status==='adding'?'tierAdding':status==='choose'?'tierChoose':status==='reached'?'tierReached':'tierLocked');
        label.classList.toggle('visually-hidden',!['selected','adding'].includes(status));
        const action=row.querySelector('[data-fg-tier-action]');action.hidden=status!=='choose';action.disabled=busy;action.onclick=()=>this.open();
      }
      this.querySelector('[data-fg-error]').hidden=true;this.querySelector('[data-fg-retry]').hidden=true;
    }
    async open() {
      if(busy||cartChanging||restoreSections)return;
      await sync(true);
      if (!this.isConnected) { [...components].find(component=>component.isConnected)?.open(); return; }
      if (!this.tier) return;
      this.selected.clear();
      this.querySelector('[data-fg-picker-error]').hidden=true;
      const missing=missingRewardGroups(state,config.catalog).length;
      this.querySelector('[data-fg-title]').textContent=state.gifts.length&&missing
        ?t(missing===1?'chooseNewGift':'chooseMoreGifts',{count:missing})
        :t(this.tier===249900?'chooseUpTo3':this.total===4?'choose4':this.total===3?'choose3':this.total===2?'choose2':'choose');
      this.querySelector('[data-fg-instruction]').textContent=t(state.gifts.length&&missing?'instructionUpgrade':'instruction'+this.tier/100);
      this.querySelector('[data-fg-selected]').open=!!state.gifts.length&&missing>0;
      for(const line of state.gifts) {
        const gift=config.catalog.find(item=>String(item.variant)===String(line.variant_id||line.id));
        if(gift)this.selected.set(this.selectionKey(gift),{gift,brand:line.properties?.['Phone Brand'],model:line.properties?.['Phone Model']});
      }
      this.querySelector('[data-fg-search]').value='';
      this.activeGroup=state.gifts.length?missingRewardGroups(state,config.catalog)[0]||'all':'all';
      this.renderCategories();
      this.renderGiftGroups();
      this.updateSelection();
      if(!this.dialog.open)this.dialog.showModal();
    }
    renderCategories() {
      const container=this.querySelector('[data-fg-categories]');container.replaceChildren();
      const groups=rewardGroups(this.tier);
      for(const group of ['all',...groups]){
        const count=config.catalog.filter(gift=>giftAllowed(gift)&&groups.includes(gift.group)&&(group==='all'||gift.group===group)).length;
        const button=node('button','fg-category');button.type='button';button.dataset.group=group;
        button.append(node('span','',t(group==='all'?'allGifts':group==='plaque'?'plaqueTab':group)),node('span','fg-category__count',String(count)));
        button.onclick=()=>{if(!busy){this.activeGroup=group;this.renderGiftGroups(this.querySelector('[data-fg-search]').value);}};
        container.append(button);
      }
    }
    renderGiftGroups(query='') {
      this.giftLoader?.disconnect();
      const batches=new Map();
      this.giftLoader=typeof IntersectionObserver==='function'?new IntersectionObserver(entries=>{
        for(const entry of entries)if(entry.isIntersecting)batches.get(entry.target)?.();
      },{root:this.dialog,rootMargin:'240px 0px',threshold:0}):null;
      const container=this.querySelector('[data-fg-groups]');container.replaceChildren();
      const terms=query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
      let matches=0;
      for(const group of rewardGroups(this.tier)){
        if(this.activeGroup!=='all'&&this.activeGroup!==group)continue;
        const gifts=config.catalog.filter(gift=>gift.group===group&&giftAllowed(gift)&&terms.every(term=>gift.title.toLocaleLowerCase().includes(term)));
        if(!gifts.length)continue;
        matches+=gifts.length;
        const section=node('section','fg-group');section.append(node('h3','',t(group)+' ('+gifts.length+')'));
        const grid=node('div','fg-grid');section.append(grid);let loaded=0;
        const sentinel=node('span','fg-load-sentinel');sentinel.setAttribute('aria-hidden','true');
        const append=()=>{
          gifts.slice(loaded,loaded+12).forEach(gift=>grid.append(this.card(gift)));loaded=Math.min(loaded+12,gifts.length);
          if(loaded>=gifts.length){this.giftLoader?.unobserve(sentinel);batches.delete(sentinel);sentinel.remove();}
          this.updateSelection();
        };
        section.append(sentinel);container.append(section);append();
        if(loaded<gifts.length){
          if(this.giftLoader){batches.set(sentinel,append);this.giftLoader.observe(sentinel);}
          else while(loaded<gifts.length)append();
        }
      }
      if(!matches)container.append(node('p','fg-empty',t('noMatches')));
      this.updateSelection();
    }
    selectionKey(gift) {return giftSelectionKey(this.tier,gift);}
    choicesValid() {return selectionValid(this.tier,[...this.selected.values()].map(choice=>choice.gift),state?.bonusValue||0);}
    card(gift) {
      const card=node('article','fg-card');card.dataset.variant=gift.variant;card.dataset.group=gift.group;
      const image=node('img','fg-card__image');const imageUrl=new URL(gift.image);imageUrl.searchParams.set('width','520');image.src=imageUrl.href;image.alt=gift.title;image.loading='lazy';image.decoding='async';image.width=260;image.height=260;
      const content=node('div','fg-card__content');content.append(node('h4','',gift.title));
      const price=node('p','fg-card__price');price.append(node('strong','',t('free')+' '),node('s','',money(gift.value)));content.append(price);
      if(['silicone','glass'].includes(gift.group)) {
        const brandLabel=node('label','',t('brand'));const brand=node('select','');brand.dataset.brand='';brand.add(new Option(t('chooseBrand'),''));Object.keys(config.phones).forEach(name=>brand.add(new Option(name,name)));brandLabel.append(brand);
        const modelLabel=node('label','',t('model'));const model=node('select','');model.dataset.model='';model.disabled=true;model.add(new Option(t('chooseModel'),''));modelLabel.append(model);
        // Browsing another design must not remove the already selected case.
        const deselectThisGift=()=>{if(this.selected.get(this.selectionKey(gift))?.gift.variant===gift.variant)this.deselect(this.selectionKey(gift));};
        brand.onchange=()=>{model.replaceChildren(new Option(t('chooseModel'),''));(config.phones[brand.value]||[]).forEach(name=>model.add(new Option(name,name)));model.disabled=!brand.value;deselectThisGift();};
        model.onchange=deselectThisGift;
        const saved=this.selected.get(this.selectionKey(gift));
        if(saved?.brand&&config.phones[saved.brand]?.includes(saved.model)){
          brand.value=saved.brand;model.replaceChildren(new Option(t('chooseModel'),''));
          config.phones[saved.brand].forEach(name=>model.add(new Option(name,name)));model.disabled=false;model.value=saved.model;
        }
        content.append(brandLabel,modelLabel);
      }
      const button=node('button','fg-card__select',t('select'));button.type='button';button.setAttribute('aria-pressed','false');button.onclick=()=>{
        if(busy)return;
        this.querySelector('[data-fg-picker-error]').hidden=true;
        if(this.selected.get(this.selectionKey(gift))?.gift.variant===gift.variant) {this.deselect(this.selectionKey(gift));return;}
        const brand=card.querySelector('[data-brand]')?.value, model=card.querySelector('[data-model]')?.value;
        if(['silicone','glass'].includes(gift.group)&&!config.phones[brand]?.includes(model)) {this.pickerError(t('modelRequired'));card.querySelector(!brand?'[data-brand]':'[data-model]').focus();return;}
        if(!this.selected.has(this.selectionKey(gift))&&this.selected.size>=this.total) {this.pickerError(t('limit',{count:this.total}));return;}
        const proposed=new Map(this.selected);proposed.set(this.selectionKey(gift),{gift,brand,model});
        if(this.tier===249900) {
          const gifts=[...proposed.values()].map(choice=>choice.gift);
          if(gifts.reduce((sum,item)=>sum+item.value,state?.bonusValue||0)>giftCap){this.pickerError(t('valueLimit'));return;}
        }
        this.selected=proposed;this.updateSelection();
      };
      content.append(button);card.append(image,content);return card;
    }
    deselect(group) {this.selected.delete(group);this.updateSelection();}
    updateSelection() {
      for(const card of this.querySelectorAll('.fg-card')) {
        const selected=this.selected.get(this.selectionKey({variant:card.dataset.variant,group:card.dataset.group}))?.gift.variant===card.dataset.variant;
        card.classList.toggle('is-selected',selected);
        const button=card.querySelector('button');button.textContent=t(selected?'chosen':'select');button.setAttribute('aria-pressed',String(selected));
      }
      const giftValue=[...this.selected.values()].reduce((sum,choice)=>sum+choice.gift.value,state?.bonusValue||0);
      this.querySelector('[data-fg-count]').textContent=t(this.tier===249900?'countUpTo':'count',{count:this.selected.size,total:this.total});
      const value=this.querySelector('[data-fg-value]');value.hidden=this.tier!==249900;value.textContent=t('valueCount',{amount:money(giftValue)});
      for(const button of this.querySelectorAll('.fg-category'))button.setAttribute('aria-pressed',String(button.dataset.group===this.activeGroup));
      const summary=this.querySelector('[data-fg-selected]');summary.hidden=!this.selected.size;
      this.querySelector('[data-fg-selected-label]').textContent=t('selectionSummary',{count:this.selected.size});
      const choices=this.querySelector('[data-fg-selections]');choices.replaceChildren();
      for(const [key,{gift,model}] of this.selected){
        const button=node('button','fg-selected__item');button.type='button';button.disabled=busy;button.setAttribute('aria-label',t('removeGift',{name:gift.title}));
        const text=node('span','');text.append(node('strong','',gift.title));if(model)text.append(node('small','',model));
        button.append(text,node('span','fg-selected__remove','×'));button.onclick=()=>{if(!busy)this.deselect(key);};choices.append(button);
      }
      const button=this.querySelector('[data-fg-add]');button.disabled=busy||!this.choicesValid();button.textContent=t('add');
    }
    pickerError(message) {const error=this.querySelector('[data-fg-picker-error]');error.textContent=message;error.hidden=false;}
    async add() {
      if(busy||cartChanging||restoreSections||!this.choicesValid())return;
      if(pending)await pending;
      if(busy||!this.isConnected||!this.choicesValid())return;
      if(recovery){this.pickerError(t('recoveryPending'));schedule();return;}
      clearTimeout(this.searchTimer);
      busy=true;lockCheckout();const button=this.querySelector('[data-fg-add]');button.disabled=true;button.textContent=t('checking');
      const controls=[...this.dialog.querySelectorAll('.fg-card button,.fg-card select,[data-fg-close],[data-fg-search],.fg-category,.fg-more,[data-fg-selections] button')].filter(element=>!element.disabled);
      controls.forEach(element=>element.disabled=true);
      try {
        const choices=[...this.selected.values()];
        await Promise.all(choices.map(async ({gift}) => {
          let product;
          try {product=await request('products/'+encodeURIComponent(gift.handle)+'.js');}catch {throw new Error(t('unavailable'));}
          const variant=product.variants?.find(variant=>String(variant.id)===gift.variant);
          if(!variant?.available||Number(variant.price)!==0)throw new Error(t('unavailable'));
        }));
        button.textContent=t('adding');
        const cart=await replaceGiftSelection({request,tier:this.tier,catalog:config.catalog,phones:config.phones,transaction:crypto.randomUUID(),saveRecovery,clearRecovery,
          choices:choices.map(({gift,brand,model})=>({id:gift.variant,quantity:1,properties:{_festive_gift:'true',_festive_tier:String(this.tier/100),_festive_group:gift.group,'Festive reward':'FREE GIFT',...(brand?{'Phone Brand':brand,'Phone Model':model}:{})}}))});
        this.dialog.close();
        state=rewardState(cart,config.catalog);
        await refreshSections();
        applyCart(cart);
      } catch(e) {
        if(e.cart)applyCart(e.cart);
        this.pickerError(t(e.recoveryPending?'recoveryPending':e.tierChanged?'changed':e.unavailable?'previousUnavailable':e.restored?'replacementRestored':'failed'));
      }
      finally {busy=false;controls.forEach(element=>element.disabled=false);lockCheckout();this.updateSelection();schedule();}
    }
  }
  if(!customElements.get('festive-rewards'))customElements.define('festive-rewards',FestiveRewards);
  document.addEventListener('cart:mutation-start',event=>{
    const plan=!busy&&!recovery&&!pending&&!cartDirty&&!restoreSections&&latestCart?planCartQuantity(latestCart,event.detail.key,event.detail.quantity,config.catalog,config.phones):null;
    revision++;cartChanging=true;cartDirty=true;
    if(plan){
      event.detail.updates=plan.updates;
      state=rewardState(plan.cart,config.catalog);
      syncCartCount(plan.cart.item_count);
      components.forEach(component=>component.render(state));
      for(const row of document.querySelectorAll('.cart-items__table-row[data-key]'))if(plan.removedGiftKeys.includes(row.dataset.key))row.setAttribute('data-fg-pending-remove','');
    }
    lockCheckout();
  });
  document.addEventListener('cart:mutation-end',event=>{
    cartChanging=false;
    if(!event.detail.success){restoreSections=true;cartDirty=true;mutationNotice=t('network');}
    lockCheckout();
    if(restoreSections||queued||(state&&(invalidGiftKeys(state,config.catalog,config.phones).length||(state.bonusExpected&&!state.bonus.length))))schedule();
  });
  document.addEventListener('cart:update',event=>{
    revision++;
    const resource=event.detail?.resource;
    const cart=Array.isArray(resource?.items)&&typeof resource.item_count==='number'&&typeof resource.total_price==='number'
      ?resource:cartFromSections(event.detail?.data?.sections);
    if (Array.isArray(cart?.items) && typeof cart.item_count==='number' && typeof cart.total_price==='number') {
      clearTimeout(timer);
      applyCart(cart);
      if(invalidGiftKeys(state,config.catalog,config.phones).length || (state.bonusExpected&&!state.bonus.length))schedule();
    } else {cartDirty=true;schedule();}
  });
  document.addEventListener('shopify:section:load',schedule);
  document.addEventListener('visibilitychange',()=>{if(!document.hidden){cartDirty=true;schedule();}});
  window.addEventListener('pageshow',()=>{cartDirty=true;schedule();});
  if(recovery){lockCheckout();schedule();}
}
